import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAssessmentStatus } from '../hooks/useAssessment';
import type { AssessmentSummary } from '../types';

export const MyResultsPage: React.FC = () => {
  const { data: assessmentStatus, isLoading, error } = useAssessmentStatus();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadgeColor = (score: number) => {
    if (score >= 80) return 'bg-green-100 text-green-800 border-green-200';
    if (score >= 60) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <Card>
            <div className="text-center py-12">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-primary-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">Carregando seus resultados</h2>
              <p className="text-slate-600">Por favor, aguarde...</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <Card>
            <div className="text-center py-12">
              <div className="w-12 h-12 bg-gradient-to-br from-red-100 to-red-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">Erro ao carregar resultados</h2>
              <p className="text-red-600 mb-4">Erro ao carregar dados</p>
              <Button onClick={() => window.location.reload()}>
                Tentar novamente
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (!assessmentStatus?.hasCompleted) {
    return (
      <div className="space-y-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <Card className="bg-gradient-to-r from-primary-50 to-secondary-50 border-primary-100">
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-slate-900">
                  Meus Resultados
                </h1>
              </div>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Histórico de suas avaliações de comunicação
              </p>
            </div>
          </Card>

          {/* Empty State */}
          <Card>
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">Nenhuma avaliação concluída</h2>
              <p className="text-slate-600 mb-6">
                Você ainda não possui avaliações concluídas. Faça sua primeira avaliação para começar a acompanhar seu desenvolvimento.
              </p>
              <Link to="/assessment">
                <Button>
                  Fazer primeira avaliação
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const completedAssessment = assessmentStatus.completedAssessment;
  
  return (
    <div className="space-y-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <Card className="bg-gradient-to-r from-primary-50 to-secondary-50 border-primary-100">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-slate-900">
                Meus Resultados
              </h1>
            </div>
            <p className="text-slate-600 max-w-2xl mx-auto">
              1 avaliação concluída
            </p>
          </div>
        </Card>

        {/* Results List */}
        <div className="space-y-4">
          {completedAssessment && (
            <Card key={completedAssessment.id}>
              <div className="text-center space-y-4 p-2">
                <div className="w-14 h-14 bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-primary-500/10">
                  <svg className="w-7 h-7 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold text-slate-900 text-lg">
                    {completedAssessment.dominant_profile || 'Perfil não definido'}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Perfil secundário: {completedAssessment.secondary_profile || 'Não definido'}
                  </p>
                </div>
                
                <div className="flex justify-center">
                  <div className="text-center">
                    <div className={`px-4 py-2 rounded-full text-lg font-bold ${getScoreBadgeColor(completedAssessment.overall_score || 0)}`}>
                      {Math.round(completedAssessment.overall_score || 0)}%
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Aderência ao perfil</p>
                  </div>
                </div>
                
                {/* Assessment Details Preview */}
                <div className="bg-slate-50 rounded-lg p-3 text-left">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-500">Concluída em</span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-slate-900">
                      {completedAssessment.completed_at ? formatDate(completedAssessment.completed_at) : 'Data não disponível'}
                    </p>
                    <p className="text-xs text-slate-600">
                      Criada em {completedAssessment.created_at ? formatDate(completedAssessment.created_at) : 'Data não disponível'}
                    </p>
                  </div>
                </div>

                <div className="pt-2">
                  <Link to={`/results/${completedAssessment.id}`}>
                    <Button variant="outline" size="sm" className="w-full font-medium">
                      Ver detalhes
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};